import React, { Component } from 'react';
import Axios from 'axios';
import { getUser } from '../../actions/session';
import { TRAFFIC_STATS_API, ENTITY_TRAFFIC_STATS_API } from '../../config/rest_endpoints';
import CustomLoader from '../common/loader';
import Container from '../common/application_container';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { fmtInt } from '../common/formatters';

const CHART_COLORS = [
    '#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED',
    '#DB2777', '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
    '#BE185D', '#0D9488', '#CA8A04', '#9333EA', '#E11D48',
    '#0284C7', '#16A34A', '#F59E0B', '#8B5CF6', '#EC4899'
];

const PERIOD_PRESETS = [
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'last15Days', label: 'Last 15 Days' },
    { key: 'thisHalfMonth', label: 'This Half Month' },
    { key: 'thisMonth', label: 'This Month' },
];

class TrafficStatsReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            User: getUser(),
            activeTab: 'account',
            startDate: moment().utc().startOf('day'),
            endDate: moment().utc(),
            quickDatePreset: 'today',
            data: [],
            isGetting: false,
            isBtnSpin: false,
            viewMode: 'sessions',
            sortColumn: 'maxSessions',
            sortDirection: 'desc',
            // Individual entity view
            selectedEntity: null,
            entityData: [],
            entityPeriod: 'lastWeek',
            isEntityLoading: false,
        };
        this.handleDate = this.handleDate.bind(this);
        this.handleQuickDateSelect = this.handleQuickDateSelect.bind(this);
        this.fetchTrafficStats = this.fetchTrafficStats.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.handleViewModeChange = this.handleViewModeChange.bind(this);
        this.handleSort = this.handleSort.bind(this);
        this.handleEntityClick = this.handleEntityClick.bind(this);
        this.handleEntityPeriodChange = this.handleEntityPeriodChange.bind(this);
        this.closeEntityView = this.closeEntityView.bind(this);
    }

    componentDidMount() {
        this.fetchTrafficStats();
    }

    handleDate(date, value) {
        if (value === 'start') {
            this.setState({ startDate: date, quickDatePreset: '' });
        } else {
            this.setState({ endDate: date, quickDatePreset: '' });
        }
    }

    handleQuickDateSelect(preset) {
        let startDate, endDate;
        switch (preset) {
            case 'today':
                startDate = moment().utc().startOf('day');
                endDate = moment().utc();
                break;
            case 'yesterday':
                startDate = moment().utc().subtract(1, 'day').startOf('day');
                endDate = moment().utc().subtract(1, 'day').endOf('day');
                break;
            case 'thisWeek':
                startDate = moment().utc().startOf('isoWeek');
                endDate = moment().utc();
                break;
            case 'thisMonth':
                startDate = moment().utc().startOf('month');
                endDate = moment().utc();
                break;
            default:
                return;
        }
        this.setState({ startDate, endDate, quickDatePreset: preset });
    }

    handleTabChange(tab) {
        this.setState({ activeTab: tab, data: [], selectedEntity: null, entityData: [] }, () => {
            this.fetchTrafficStats();
        });
    }

    handleViewModeChange(mode) {
        this.setState({ viewMode: mode });
    }

    handleSort(column) {
        const { sortColumn, sortDirection } = this.state;
        let newDirection = 'desc';
        if (sortColumn === column && sortDirection === 'desc') {
            newDirection = 'asc';
        }
        this.setState({ sortColumn: column, sortDirection: newDirection });
    }

    fetchTrafficStats() {
        const { User, activeTab, startDate, endDate } = this.state;
        var payload = {
            type: activeTab,
            startDate: moment(startDate).format('MM/DD/YYYY, h:mm:ss A'),
            endDate: moment(endDate).format('MM/DD/YYYY, h:mm:ss A'),
        };
        this.setState({ isGetting: true, isBtnSpin: true });
        var headers = { 'Authorization': 'Bearer ' + User.token };
        Axios.post(TRAFFIC_STATS_API, payload, { headers }).then(res => {
            if (res) {
                var resp = res.data.data || [];
                this.setState({ data: resp, isGetting: false, isBtnSpin: false });
            }
        }).catch(err => {
            console.error('Error fetching traffic stats:', err);
            this.setState({ isGetting: false, isBtnSpin: false });
        });
    }

    handleEntityClick(entityId, entityName) {
        this.setState({
            selectedEntity: { id: entityId, name: entityName },
            entityPeriod: 'lastWeek',
        }, () => {
            this.fetchEntityTrafficStats();
        });
    }

    handleEntityPeriodChange(period) {
        this.setState({ entityPeriod: period }, () => {
            this.fetchEntityTrafficStats();
        });
    }

    closeEntityView() {
        this.setState({ selectedEntity: null, entityData: [] });
    }

    fetchEntityTrafficStats() {
        const { User, activeTab, selectedEntity, entityPeriod } = this.state;
        if (!selectedEntity) return;

        var payload = {
            type: activeTab,
            entityId: selectedEntity.id,
            period: entityPeriod,
        };
        this.setState({ isEntityLoading: true });
        var headers = { 'Authorization': 'Bearer ' + User.token };
        Axios.post(ENTITY_TRAFFIC_STATS_API, payload, { headers }).then(res => {
            if (res) {
                var resp = res.data.data || [];
                this.setState({ entityData: resp, isEntityLoading: false });
            }
        }).catch(err => {
            console.error('Error fetching entity traffic stats:', err);
            this.setState({ isEntityLoading: false });
        });
    }

    // Aggregate snapshots into adaptive intervals:
    // - Keep 10-min granularity during active traffic hours
    // - Collapse into single hourly point during low/zero traffic hours
    aggregateData(rawData) {
        if (!rawData || rawData.length === 0) return rawData;

        // Sort by timestamp
        var sorted = rawData.slice().sort(function(a, b) {
            return moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf();
        });

        // Group all snapshots by hour
        var hourBuckets = {};
        sorted.forEach(function(item) {
            var hourKey = moment(item.timestamp).startOf('hour').toISOString();
            if (!hourBuckets[hourKey]) {
                hourBuckets[hourKey] = [];
            }
            hourBuckets[hourKey].push(item);
        });

        // Find the peak session value per entity across ALL data to set threshold
        var entityPeaks = {};
        sorted.forEach(function(item) {
            var key = item.entityName || item.entityId;
            if (!entityPeaks[key] || item.sessions > entityPeaks[key]) {
                entityPeaks[key] = item.sessions;
            }
        });
        var overallPeak = 0;
        Object.values(entityPeaks).forEach(function(v) {
            if (v > overallPeak) overallPeak = v;
        });
        // Threshold: an hour is "low traffic" if the max single snapshot is < 5% of peak (or < 5 sessions)
        var threshold = Math.max(5, Math.round(overallPeak * 0.05));

        var result = [];
        var hourKeys = Object.keys(hourBuckets).sort();

        hourKeys.forEach(function(hourKey) {
            var items = hourBuckets[hourKey];

            // Find max session value in any single snapshot this hour
            var maxInHour = 0;
            items.forEach(function(item) {
                if (item.sessions > maxInHour) maxInHour = item.sessions;
            });

            if (maxInHour >= threshold) {
                // Active hour — keep original 10-min granularity
                items.forEach(function(item) { result.push(item); });
            } else {
                // Low-traffic hour — collapse to one point per entity at the hour mark
                var entityAgg = {};
                items.forEach(function(item) {
                    var key = item.entityName || item.entityId;
                    if (!entityAgg[key]) {
                        entityAgg[key] = {
                            entityId: item.entityId,
                            entityName: item.entityName,
                            timestamp: hourKey,
                            type: item.type,
                            sessions: 0,
                            cps: 0,
                        };
                    }
                    // Keep the max values for the hour
                    if (item.sessions > entityAgg[key].sessions) entityAgg[key].sessions = item.sessions;
                    if (item.cps > entityAgg[key].cps) entityAgg[key].cps = item.cps;
                });
                Object.values(entityAgg).forEach(function(agg) { result.push(agg); });
            }
        });

        return result;
    }

    buildChartData() {
        const { data, viewMode } = this.state;
        if (!data || data.length === 0) {
            return { labels: [], datasets: [] };
        }

        // Apply adaptive aggregation to reduce low-traffic noise
        var aggregated = this.aggregateData(data);

        const entityMap = {};
        const timestampSet = new Set();

        aggregated.forEach(item => {
            const key = item.entityName || item.entityId;
            if (!entityMap[key]) {
                entityMap[key] = {};
            }
            const ts = moment(item.timestamp).format('MM/DD HH:mm');
            timestampSet.add(ts);
            entityMap[key][ts] = viewMode === 'sessions' ? item.sessions : item.cps;
        });

        const labels = Array.from(timestampSet).sort((a, b) => {
            return moment(a, 'MM/DD HH:mm').valueOf() - moment(b, 'MM/DD HH:mm').valueOf();
        });

        const entityNames = Object.keys(entityMap).filter(name => {
            return Object.values(entityMap[name]).some(v => v > 0);
        }).sort();

        const datasets = entityNames.map((name, idx) => {
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            return {
                label: name,
                data: labels.map(ts => entityMap[name][ts] || 0),
                borderColor: color,
                backgroundColor: color + '10',
                fill: false,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: color,
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 2,
                borderWidth: 2,
                lineTension: 0.3,
            };
        });

        return { labels, datasets };
    }

    buildEntityChartData() {
        const { entityData, viewMode, selectedEntity } = this.state;
        if (!entityData || entityData.length === 0) {
            return { labels: [], datasets: [] };
        }

        // Apply adaptive aggregation
        var aggregated = this.aggregateData(entityData);

        const labels = [];
        const sessionsData = [];
        const cpsData = [];

        aggregated.forEach(item => {
            labels.push(moment(item.timestamp).format('MM/DD HH:mm'));
            sessionsData.push(item.sessions);
            cpsData.push(item.cps);
        });

        const datasets = [];
        if (viewMode === 'sessions') {
            datasets.push({
                label: (selectedEntity ? selectedEntity.name : '') + ' Sessions',
                data: sessionsData,
                borderColor: '#2563EB',
                backgroundColor: '#2563EB10',
                fill: true,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: '#2563EB',
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 2,
                borderWidth: 2,
                lineTension: 0.3,
            });
        } else {
            datasets.push({
                label: (selectedEntity ? selectedEntity.name : '') + ' CPS',
                data: cpsData,
                borderColor: '#DC2626',
                backgroundColor: '#DC262610',
                fill: true,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: '#DC2626',
                pointHoverBorderColor: '#fff',
                pointHoverBorderWidth: 2,
                borderWidth: 2,
                lineTension: 0.3,
            });
        }

        return { labels, datasets };
    }

    buildSummaryData() {
        const { data } = this.state;
        const { sortColumn, sortDirection } = this.state;
        if (!data || data.length === 0) return [];

        const summaryMap = {};
        data.forEach(item => {
            const key = item.entityName || item.entityId;
            if (!summaryMap[key]) {
                summaryMap[key] = { name: key, entityId: item.entityId, totalSessions: 0, totalCPS: 0, count: 0, maxSessions: 0, maxCPS: 0 };
            }
            summaryMap[key].totalSessions += item.sessions;
            summaryMap[key].totalCPS += item.cps;
            summaryMap[key].count += 1;
            if (item.sessions > summaryMap[key].maxSessions) summaryMap[key].maxSessions = item.sessions;
            if (item.cps > summaryMap[key].maxCPS) summaryMap[key].maxCPS = item.cps;
        });

        let result = Object.values(summaryMap)
            .filter(item => item.maxSessions > 0 || item.maxCPS > 0)
            .map(item => ({
                ...item,
                avgSessions: item.count > 0 ? Math.round(item.totalSessions / item.count) : 0,
                avgCPS: item.count > 0 ? Math.round(item.totalCPS / item.count) : 0,
            }));

        // Sort
        result.sort((a, b) => {
            let valA, valB;
            if (sortColumn === 'name') {
                valA = a.name.toLowerCase();
                valB = b.name.toLowerCase();
                if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
                if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }
            valA = a[sortColumn] || 0;
            valB = b[sortColumn] || 0;
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        });

        return result;
    }

    renderSortIcon(column) {
        const { sortColumn, sortDirection } = this.state;
        if (sortColumn !== column) {
            return <i className="icon-sort ml-1" style={{ fontSize: '10px', opacity: 0.3 }}></i>;
        }
        return sortDirection === 'asc'
            ? <i className="icon-sort-amount-asc ml-1" style={{ fontSize: '10px' }}></i>
            : <i className="icon-sort-amount-desc ml-1" style={{ fontSize: '10px' }}></i>;
    }

    renderEntityDetailView() {
        const { selectedEntity, entityPeriod, isEntityLoading, viewMode } = this.state;
        if (!selectedEntity) return null;

        const entityChartData = this.buildEntityChartData();

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                datalabels: { display: false }
            },
            legend: {
                position: 'bottom',
                labels: { fontSize: 11, boxWidth: 12, padding: 15, usePointStyle: true }
            },
            tooltips: {
                mode: 'nearest',
                intersect: false,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                titleFontSize: 11,
                bodyFontSize: 12,
                bodySpacing: 4,
                padding: 10,
                cornerRadius: 6,
                caretSize: 5,
            },
            hover: { mode: 'nearest', intersect: false, animationDuration: 150 },
            scales: {
                xAxes: [{
                    gridLines: { display: false },
                    ticks: { fontSize: 10, maxRotation: 45, autoSkipPadding: 10 }
                }],
                yAxes: [{
                    gridLines: { color: '#F1F5F9', drawBorder: false },
                    ticks: { fontSize: 10, beginAtZero: true, padding: 8 },
                    scaleLabel: {
                        display: true,
                        labelString: viewMode === 'sessions' ? 'Sessions' : 'CPS',
                        fontSize: 11,
                        fontStyle: '600',
                    }
                }]
            }
        };

        return (
            <div style={{ marginTop: '24px', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px', background: '#F8FAFC' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h6 style={{ fontWeight: '600', margin: 0 }}>
                        <i className="fa fa-line-chart mr-2"></i>
                        {selectedEntity.name} - Traffic Detail
                    </h6>
                    <button className="btn btn-sm btn-outline-secondary" onClick={this.closeEntityView}>
                        <i className="fa fa-times mr-1"></i> Close
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    {PERIOD_PRESETS.map(p => (
                        <button
                            key={p.key}
                            className={`btn btn-sm ${entityPeriod === p.key ? 'btn-info' : 'btn-outline-secondary'}`}
                            onClick={() => this.handleEntityPeriodChange(p.key)}
                            style={{ fontSize: '12px' }}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {isEntityLoading ? (
                    <CustomLoader />
                ) : (
                    <div style={{ height: '350px' }}>
                        {entityChartData.datasets.length > 0 ? (
                            <Line data={entityChartData} options={chartOptions} />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8' }}>
                                <p>No data available for this period.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { activeTab, startDate, endDate, quickDatePreset, isGetting, isBtnSpin, viewMode } = this.state;
        const chartData = this.buildChartData();
        const summaryData = this.buildSummaryData();

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                datalabels: { display: false }
            },
            legend: {
                position: 'bottom',
                labels: {
                    fontSize: 11,
                    boxWidth: 12,
                    padding: 15,
                    usePointStyle: true,
                }
            },
            tooltips: {
                mode: 'nearest',
                intersect: false,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                titleFontSize: 11,
                bodyFontSize: 12,
                bodySpacing: 4,
                padding: 10,
                cornerRadius: 6,
                caretSize: 5,
                callbacks: {
                    title: function(tooltipItems) {
                        return tooltipItems[0].xLabel;
                    },
                    label: function(tooltipItem, data) {
                        var label = data.datasets[tooltipItem.datasetIndex].label || '';
                        return '  ' + label + ': ' + tooltipItem.yLabel;
                    }
                }
            },
            hover: {
                mode: 'nearest',
                intersect: false,
                animationDuration: 150,
            },
            elements: {
                line: {
                    borderCapStyle: 'round',
                    borderJoinStyle: 'round',
                }
            },
            scales: {
                xAxes: [{
                    gridLines: { display: false },
                    ticks: { fontSize: 10, maxRotation: 45, autoSkipPadding: 10 }
                }],
                yAxes: [{
                    gridLines: { color: '#F1F5F9', drawBorder: false },
                    ticks: { fontSize: 10, beginAtZero: true, padding: 8 },
                    scaleLabel: {
                        display: true,
                        labelString: viewMode === 'sessions' ? 'Sessions' : 'CPS',
                        fontSize: 11,
                        fontStyle: '600',
                    }
                }]
            }
        };

        const presetButtons = [
            { key: 'today', label: 'Today' },
            { key: 'yesterday', label: 'Yesterday' },
            { key: 'thisWeek', label: 'This Week' },
            { key: 'thisMonth', label: 'This Month' },
        ];

        const thStyle = { textAlign: 'left', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: '600' };
        const thStyleRight = { textAlign: 'right', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '12px', fontWeight: '600' };
        const tdStyle = { textAlign: 'left', fontSize: '12px' };
        const tdStyleRight = { textAlign: 'right', fontSize: '12px' };

        return (
            <Container title="Traffic" title1="Stats" icon="fa fa-line-chart">
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '16px', background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                    <div style={{ padding: '20px' }}>

                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                            <button
                                className={`btn btn-sm ${activeTab === 'account' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => this.handleTabChange('account')}
                                style={{ fontWeight: '600' }}
                            >
                                Account Traffic
                            </button>
                            <button
                                className={`btn btn-sm ${activeTab === 'carrier' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => this.handleTabChange('carrier')}
                                style={{ fontWeight: '600' }}
                            >
                                Carrier Traffic
                            </button>
                            <button
                                className={`btn btn-sm ${activeTab === 'system' ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => this.handleTabChange('system')}
                                style={{ fontWeight: '600' }}
                            >
                                System Traffic
                            </button>
                        </div>

                        {/* Date Controls */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                            {presetButtons.map(p => (
                                <button
                                    key={p.key}
                                    className={`btn btn-sm ${quickDatePreset === p.key ? 'btn-info' : 'btn-outline-secondary'}`}
                                    onClick={() => this.handleQuickDateSelect(p.key)}
                                    style={{ fontSize: '12px' }}
                                >
                                    {p.label}
                                </button>
                            ))}

                            <span style={{ color: '#94A3B8', fontSize: '12px' }}>|</span>

                            <DateRangePicker
                                onApply={(e, picker) => this.handleDate(picker.startDate, 'start')}
                                initialSettings={{ singleDatePicker: true, startDate: moment(startDate) }}
                            >
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ width: '140px', fontSize: '12px' }}
                                    value={moment(startDate).format('MM/DD/YYYY')}
                                    readOnly
                                />
                            </DateRangePicker>

                            <span style={{ color: '#94A3B8', fontSize: '12px' }}>to</span>

                            <DateRangePicker
                                onApply={(e, picker) => this.handleDate(picker.startDate, 'end')}
                                initialSettings={{ singleDatePicker: true, startDate: moment(endDate) }}
                            >
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ width: '140px', fontSize: '12px' }}
                                    value={moment(endDate).format('MM/DD/YYYY')}
                                    readOnly
                                />
                            </DateRangePicker>

                            <button
                                className="btn btn-primary btn-sm"
                                onClick={this.fetchTrafficStats}
                                style={{ fontWeight: '600' }}
                            >
                                Search <i className={`${isBtnSpin ? 'icon-spinner4 spinner' : 'icon-arrow-right8'} ml-2`}></i>
                            </button>
                        </div>

                        {/* View Mode Toggle */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                            <button
                                className={`btn btn-sm ${viewMode === 'sessions' ? 'btn-success' : 'btn-outline-secondary'}`}
                                onClick={() => this.handleViewModeChange('sessions')}
                                style={{ fontSize: '12px' }}
                            >
                                Sessions
                            </button>
                            <button
                                className={`btn btn-sm ${viewMode === 'cps' ? 'btn-success' : 'btn-outline-secondary'}`}
                                onClick={() => this.handleViewModeChange('cps')}
                                style={{ fontSize: '12px' }}
                            >
                                CPS
                            </button>
                        </div>

                        {/* Chart */}
                        {isGetting ? (
                            <CustomLoader />
                        ) : (
                            <div style={{ height: '400px', marginBottom: '24px' }}>
                                {chartData.datasets.length > 0 ? (
                                    <Line data={chartData} options={chartOptions} />
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8' }}>
                                        <p>No data available for the selected date range. Data is collected every 10 minutes.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Summary Table */}
                        {summaryData.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                                <h6 style={{ fontWeight: '600', marginBottom: '12px' }}>Summary</h6>
                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-striped table-sm">
                                        <thead>
                                            <tr>
                                                <th style={thStyle} onClick={() => this.handleSort('name')}>
                                                    Name {this.renderSortIcon('name')}
                                                </th>
                                                <th style={thStyleRight} onClick={() => this.handleSort('maxSessions')}>
                                                    Max Sessions {this.renderSortIcon('maxSessions')}
                                                </th>
                                                <th style={thStyleRight} onClick={() => this.handleSort('avgSessions')}>
                                                    Avg Sessions {this.renderSortIcon('avgSessions')}
                                                </th>
                                                <th style={thStyleRight} onClick={() => this.handleSort('maxCPS')}>
                                                    Max CPS {this.renderSortIcon('maxCPS')}
                                                </th>
                                                <th style={thStyleRight} onClick={() => this.handleSort('avgCPS')}>
                                                    Avg CPS {this.renderSortIcon('avgCPS')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summaryData.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td style={tdStyle}>
                                                        <span
                                                            className="badge bg-blue fs-12"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => this.handleEntityClick(item.entityId, item.name)}
                                                            title="Click to view individual traffic"
                                                        >
                                                            {item.name} <i className="fa fa-external-link ml-1" style={{ fontSize: '9px' }}></i>
                                                        </span>
                                                    </td>
                                                    <td style={tdStyleRight}>{fmtInt(item.maxSessions)}</td>
                                                    <td style={tdStyleRight}>{fmtInt(item.avgSessions)}</td>
                                                    <td style={tdStyleRight}>{fmtInt(item.maxCPS)}</td>
                                                    <td style={tdStyleRight}>{fmtInt(item.avgCPS)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Entity Detail View */}
                        {this.renderEntityDetailView()}

                    </div>
                </div>
            </Container>
        );
    }
}

export default TrafficStatsReport;
